import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function googleLogin() {
  const redirectUrl = window.location.origin + "/";
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-zinc-800 bg-[#121212]">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#faff69] grid place-items-center"><Sparkles className="h-5 w-5 text-black" /></div>
          <span className="font-display font-bold text-xl tracking-tight">FAL STUDIO</span>
        </div>
        <div>
          <h2 className="font-display text-4xl font-bold tracking-tight leading-[1.05]">Your personal<br /><span className="text-[#faff69]">generative</span> workstation.</h2>
          <p className="text-zinc-400 mt-4 max-w-sm text-sm leading-relaxed">Image + video creation across the latest fal.ai models. Your key, history and gallery sync securely across every device.</p>
        </div>
        <p className="font-mono text-[11px] text-zinc-600 uppercase tracking-[0.2em]">local render console</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-zinc-400 text-sm mt-1.5">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); navigate("/", { replace: true }); }
    catch (err) { toast.error(fmtErr(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your studio">
      <Button onClick={googleLogin} data-testid="google-login-btn"
        className="w-full bg-white text-black hover:bg-zinc-200 font-medium">
        <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-4 w-4 mr-2" />
        Continue with Google
      </Button>
      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-zinc-800" /><span className="text-[11px] text-zinc-600 font-mono">OR</span><div className="h-px flex-1 bg-zinc-800" />
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="bg-[#121212] border-zinc-800 focus-visible:ring-[#faff69]" />
        <Input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="bg-[#121212] border-zinc-800 focus-visible:ring-[#faff69]" />
        <Button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full bg-[#faff69] text-black hover:bg-[#e6eb52] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
      <div className="mt-3 text-center">
        <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-[#faff69]" data-testid="to-forgot">Forgot your password?</Link>
      </div>
      <p className="text-sm text-zinc-400 mt-5">No account? <Link to="/register" className="text-[#faff69] hover:underline" data-testid="to-register">Create one</Link></p>
    </AuthShell>
  );
}
